import { BillableIcon, Button, Currency, Field, Hours, Icon, Table } from '~/components';
import { TableBoxRowActions } from '~/components/table';
import { Formik } from 'formik';
import _ from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { emptyStringToNull, mergeValues } from '~/utils';
import * as Yup from 'yup';
import { ProjectTaskFormContext } from './ProjectTaskForm';

function ProjectTaskRolesTable({ project, task, value, onChange }) {
  const [editIndex, setEditIndex] = useState(null);

  const handleSubmit = (item) => {
    const valueItem = value.find((v) => v.projectRoleId === item.projectRoleId);
    if (valueItem) {
      onChange(value.map((v) => (v.projectRoleId === item.projectRoleId ? item : v)));
    } else {
      onChange([...value, item]);
    }

    setEditIndex(null);
  };

  const handleDelete = (index) => {
    onChange(value.filter((e, i) => i !== index));
  };

  const totalHours = _.sumBy(value, 'hours');

  const totalFees = task.isBillable
    ? _.sumBy(value, ({ hours, role }) => {
        return _.isNumber(hours) && _.isNumber(role?.actualRate) ? hours * role?.actualRate : null;
      })
    : 0;

  const currency = project.currency;

  return (
    <Table data-testid="roles_form">
      <Table.BoxHeader>
        <Table.Column>Project Role</Table.Column>
        <Table.Column align="right" width="6.5rem">
          Hours
        </Table.Column>
        <Table.Column align="right" width="8rem" isVisible={project.isBillable && project.billingTypeId === 'tm'}>
          Amount
        </Table.Column>
        <Table.BoxActionsColumn />
      </Table.BoxHeader>
      <Table.Body>
        {value.map((item, index) =>
          index === editIndex ? (
            <RoleRowForm
              key={item.role.id}
              project={project}
              task={task}
              taskRole={item}
              taskRoles={value}
              currency={currency}
              onSubmit={handleSubmit}
              onCancel={() => setEditIndex(null)}
            />
          ) : (
            <RoleRowDetails
              key={item.role.id}
              currency={currency}
              task={task}
              taskRole={item}
              disableActions={editIndex !== null}
              onEdit={() => setEditIndex(index)}
              onDelete={() => handleDelete(index)}
            />
          ),
        )}

        {editIndex === -1 ? (
          <RoleRowForm
            project={project}
            task={task}
            taskRoles={value}
            currency={currency}
            onSubmit={handleSubmit}
            onCancel={() => setEditIndex(null)}
          />
        ) : (
          <Table.Row>
            <Table.Cell>
              <Button isAnchor isStrong disabled={editIndex !== null} onClick={() => setEditIndex(-1)}>
                <Icon icon="plus" size="xs" spaceRight />
                Quick Add
              </Button>
            </Table.Cell>
            <Table.Cell>
              <Hours value={totalHours} />
            </Table.Cell>
            <Table.Cell>
              <Currency value={totalFees} currency={project.currency} />
            </Table.Cell>
            <Table.Cell />
          </Table.Row>
        )}
      </Table.Body>
    </Table>
  );
}

function RoleRowDetails({ currency, taskRole: { hours, role }, task, disableActions, onEdit, onDelete }) {
  const fees = task.isBillable && _.isNumber(hours) && _.isNumber(role?.actualRate) ? hours * role?.actualRate : null;

  return (
    <Table.BoxRow>
      <Table.Cell>
        <BillableIcon value={role.isActuallyBillable} /> {role.name}
      </Table.Cell>
      <Table.Cell align="right">
        <Hours value={hours} />
      </Table.Cell>
      <Table.Cell align="right">
        <Currency value={fees} currency={currency} />
      </Table.Cell>
      <TableBoxRowActions>
        <TableBoxRowActions.Edit disabled={disableActions} onClick={onEdit} />
        <hr />
        <TableBoxRowActions.Delete disabled={disableActions} onClick={onDelete} />
      </TableBoxRowActions>
    </Table.BoxRow>
  );
}

function RoleRowForm({ currency, project, taskRole, taskRoles, task, onSubmit, onCancel }) {
  // Submit the row if the drawer's "Save" button is clicked
  const form = useRef();
  const { setForms } = useContext(ProjectTaskFormContext);

  useEffect(() => {
    // Register the inline form
    setForms((forms) => [...forms, { id: 'role', ref: form }]);

    return () => {
      // Unregister the inline form
      setForms((forms) => forms.filter((f) => f.id !== 'role'));
    };
  }, [setForms]);

  const initialValues = mergeValues(
    {
      role: null,
      hours: '',
    },
    taskRole,
  );

  const excludedRoles = taskRoles.map(({ role }) => role.id);

  const handleSubmit = (values) => {
    const { hours, role } = emptyStringToNull(values);
    onSubmit({ hours, role, projectRoleId: role.id });
  };

  return (
    <Formik
      innerRef={form}
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={Yup.object().shape({
        role: Yup.mixed().label('Project Role').required(),
        hours: Yup.number().min(0).label('Hours').nullable(),
      })}>
      {({ values: { hours, role }, submitForm }) => {
        const fees =
          task.isBillable && _.isNumber(hours) && _.isNumber(role?.actualRate) ? hours * role?.actualRate : null;

        return (
          <Table.BoxRow focused onEnter={submitForm}>
            {taskRole ? (
              <Table.Cell>{taskRole.role.name}</Table.Cell>
            ) : (
              <Table.Cell>
                <Field.ProjectRoleSelect
                  name="role"
                  placeholder="Role"
                  project={project}
                  autoFocus={!taskRole}
                  exclude={excludedRoles}
                />
              </Table.Cell>
            )}
            <Table.Cell>
              <Field.Number
                name="hours"
                placeholder="Hrs"
                materialPlaceholder="Hours"
                min={0}
                precision={2}
                autoFocus={taskRole}
              />
            </Table.Cell>
            <Table.Cell>
              <Currency value={fees} currency={currency} />
            </Table.Cell>

            <TableBoxRowActions.Form onSubmit={submitForm} onCancel={onCancel} />
          </Table.BoxRow>
        );
      }}
    </Formik>
  );
}

export default ProjectTaskRolesTable;
